/** Calculated sensors: formulas over stored readings (workspace v5). Kept
 *  on the server since the fork reached master (db/035,
 *  services/calculated_sensors.py), in a project, whose members see them and
 *  whose Editors and Admins change them. Only the definition is kept; the
 *  values are worked out where they are drawn (`analysisSources.ts`) at the
 *  exact timestamps of the inputs, never written or served as readings
 *  (PLAN.md §8.6). The server checks every formula again with the same
 *  rules and sentences (alerting/expressions.py). */
import { api } from '../api/client'
import { createSavedViewId } from './savedViews'
import { compileMonitoringExpression, type ExpressionMode } from './monitoringExpressions'
import { remoteCollection } from './remoteStore'

export const CALCULATED_SENSORS_EVENT = 'hydro:calculated-sensors-changed'
export const MAX_CALCULATED_SENSORS = 100
export type InputAlias = 'A' | 'B' | 'C'
export interface CalculatedSensorInput { alias: InputAlias; sourceId: string }
/** What a calculation needs: a draft in the editor has this much. */
export interface CalculatedDefinition {
  id: string
  name: string
  formula: string
  mode?: ExpressionMode
  unit: string
  axis_group: string
  precision: number
  inputs: CalculatedSensorInput[]
}
/** A saved calculated sensor, as the server describes it. */
export interface CalculatedSensor extends CalculatedDefinition {
  project_id: number
  project: string
  refs: string[]
  created_at: string
  updated_at: string
  can_edit: boolean
}

export const FORMULA_TEMPLATES = [
  { name: 'Difference', formula: 'A - B' },
  { name: 'Mean', formula: '(A + B) / 2' },
  { name: 'Scale and offset', formula: 'A * 1 + 0' },
  { name: 'Resultant', formula: 'sqrt(A^2 + B^2)' },
] as const

export class FormulaError extends Error {
  constructor(message: string) { super(message); this.name = 'FormulaError' }
}

type FormulaValues = Record<string, number | null | undefined>
export interface CompiledFormula {
  formula: string
  variables: InputAlias[]
  directReferences: string[]
  evaluate: (values: FormulaValues) => number | null
}

/** Numeric wrapper around the shared expression language. Existing A/B/C
 * definitions keep the same precedence, null handling and storage format. */
export function compileFormula(formula: string, options: { mode?: ExpressionMode } = {}): CompiledFormula {
  if (options.mode !== 'program' && formula.length > 500) throw new FormulaError('Use a formula of 500 characters or fewer, or switch to Code mode.')
  try {
    const compiled = compileMonitoringExpression(formula, options)
    if (compiled.resultType !== 'number') throw new FormulaError('A calculated sensor must return a number. Use IF(condition, number, number) for conditional results.')
    const variables: InputAlias[] = [], directReferences: string[] = []
    for (const reference of compiled.references) {
      if (reference === 'A' || reference === 'B' || reference === 'C') variables.push(reference)
      else if (isRawSourceId(reference)) directReferences.push(reference)
      else throw new FormulaError('Use A, B, C or a channel reference such as [station_id|channel_name].')
    }
    if (!compiled.references.length) throw new FormulaError('Reference at least one input: A, B, C or a channel.')
    if (compiled.references.length > 16) throw new FormulaError('Use no more than 16 input references.')
    return { formula: formula.trim(), variables, directReferences,
      evaluate(values) { const value = compiled.evaluate(values); return typeof value === 'number' ? value : null } }
  } catch (cause) {
    if (cause instanceof FormulaError) throw cause
    throw new FormulaError(cause instanceof Error ? cause.message : 'Invalid formula.')
  }
}

export function evaluateFormula(formula: string, values: FormulaValues, options: { mode?: ExpressionMode } = {}): number | null {
  return compileFormula(formula, options).evaluate(values)
}
export interface CalculationSourceBinding { alias: string; sourceId: string }
export function calculationSourceBindings(sensor: CalculatedDefinition): CalculationSourceBinding[] {
  const formula = compileFormula(sensor.formula, { mode: sensor.mode })
  return [...sensor.inputs.filter(input => formula.variables.includes(input.alias)),
    ...formula.directReferences.map(sourceId => ({ alias: sourceId, sourceId }))]
}

export function calculatedSourceId(id: string): string { return 'calculated|' + id }
export function createCalculatedSensorId(): string { return createSavedViewId().replace(/^view-/, 'calc-') }
export function isRawSourceId(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 512
    && /^[^|,\s]+\|[^|,\s]+$/.test(value) && !value.startsWith('calculated|')
}

function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}
export function validateCalculatedSensor(value: unknown): string | null {
  if (!record(value)) return 'Invalid calculated sensor.'
  if (typeof value.id !== 'string' || !/^[A-Za-z0-9_-]{1,100}$/.test(value.id)) return 'Invalid sensor ID.'
  if (typeof value.name !== 'string' || !value.name.trim() || value.name.trim().length > 100) return 'Enter a name of 100 characters or fewer.'
  if (typeof value.formula !== 'string') return 'Enter a formula.'
  if (value.mode !== undefined && value.mode !== 'expression' && value.mode !== 'program') return 'Choose a valid calculation mode.'
  if (typeof value.unit !== 'string' || !value.unit.trim() || value.unit.length > 30) return 'Enter an output unit.'
  if (typeof value.axis_group !== 'string' || !/^[A-Za-z][A-Za-z0-9_-]{0,79}$/.test(value.axis_group)) return 'Enter a valid physical quantity.'
  if (!Number.isInteger(value.precision) || Number(value.precision) < 0 || Number(value.precision) > 9) return 'Choose 0 to 9 decimal places.'
  if (!Array.isArray(value.inputs) || value.inputs.length > 3) return 'Select up to three source input aliases.'
  const aliases = new Set<string>()
  for (const input of value.inputs) {
    if (!record(input) || !['A', 'B', 'C'].includes(String(input.alias)) || !isRawSourceId(input.sourceId)) return 'Select a raw sensor for each input.'
    if (aliases.has(String(input.alias))) return 'Each input alias must be unique.'
    aliases.add(String(input.alias))
  }
  try {
    const parsed = compileFormula(value.formula, { mode: value.mode as ExpressionMode | undefined })
    const missing = parsed.variables.filter(alias => !aliases.has(alias))
    if (missing.length) return 'Select source sensors for ' + missing.join(', ') + '.'
  } catch (cause) { return cause instanceof Error ? cause.message : 'Invalid formula.' }
  return null
}

// ------------------------------------------------------------ server

/** Every calculated sensor in the reader's projects, held once for every
 *  page (the editor, the Data Explorer, the Alarm Lab, workspace search). */
export const calculatedSensorStore = remoteCollection<CalculatedSensor>(
  () => api.calculatedSensors.list().then(data => data.sensors), CALCULATED_SENSORS_EVENT)

/** What is held now, for code that cannot wait. */
export function readCalculatedSensors(): CalculatedSensor[] {
  return calculatedSensorStore.snapshot().items
}

/** Save a definition in a project: a new one when `key` is absent, else the
 *  saved sensor of that key. Only the inputs its formula uses are sent. */
export async function saveCalculatedSensor(definition: CalculatedDefinition, projectId: number,
    key?: string): Promise<CalculatedSensor> {
  const invalid = validateCalculatedSensor(definition)
  if (invalid) throw new Error(invalid)
  const body = { project_id: projectId, name: definition.name.trim(), formula: definition.formula.trim(),
    mode: definition.mode ?? 'expression', unit: definition.unit.trim(), axis_group: definition.axis_group,
    precision: definition.precision, inputs: definition.inputs.map(input => ({ ...input })) }
  const { sensor } = key ? await api.calculatedSensors.update(key, body) : await api.calculatedSensors.create(body)
  await calculatedSensorStore.refresh()
  return sensor
}

export async function deleteCalculatedSensor(key: string): Promise<void> {
  await api.calculatedSensors.remove(key)
  await calculatedSensorStore.refresh()
}
